import type {PosterPageData} from '@Libraries/poster-library';
import {noop} from '@Utils/general.util';
import styles from '@Components/design-selector/design-selector.module.scss';

type PosterId = string;
type PageId = string;

const DESIGN_SELECTOR_PAGES_LIMIT = 5;

export function openDesignSelector(
  onDesignLoaded: (data: PosterPageData[]) => void,
  beforeLoad: VoidFunction = noop,
  isMultiselectAllowed = true,
  addCheckmarkOnSelection = true,
  showPageLimitWarning = true
): void {
  window.PMW.util.require(typeof window.PMW.openDesignSelectorModal === 'undefined', 'designSelector', true, () => {
    beforeLoad();
    window.PMW.openDesignSelectorModal(
      window.i18next.t('pmwjs_select_a_design'),
      onDesignLoaded,
      undefined,
      false,
      undefined,
      (data) => {
        addStylesOnSelection(data, isMultiselectAllowed, addCheckmarkOnSelection, showPageLimitWarning);
      },
      isMultiselectAllowed,
      isMultiselectAllowed
    );
  });
}

/**
 * This legacy method exists for handling designs when multipages are not enabled
 * @param onDesignLoaded
 * @param beforeLoad
 * @deprecated Please use {@link openDesignSelector} instead
 */
export function openDesignSelectorLegacy(onDesignLoaded: (id: string, imagePreviewUrl: string | undefined) => void, beforeLoad: VoidFunction = noop): void {
  window.PMW.util.require(typeof window.PMW.openDesignSelectorModalLegacy === 'undefined', 'designSelector', true, () => {
    beforeLoad();
    window.PMW.openDesignSelectorModalLegacy(window.i18next.t('pmwjs_select_a_design'), onDesignLoaded);
  });
}

function addStylesOnSelection(data: PosterPageData[], isMultiselectAllowed = true, addCheckmarkOnSelection = true, showPageLimitWarning = true): void {
  const selectedPostersMap: Record<PosterId, PageId[]> = {};

  data.forEach((posterPageData) => {
    if (selectedPostersMap[posterPageData.posterId]) {
      selectedPostersMap[posterPageData.posterId].push(posterPageData.pageId);
    } else {
      selectedPostersMap[posterPageData.posterId] = [posterPageData.pageId];
    }
  });

  if (!isMultiselectAllowed) {
    deSelectPreviouslySelectedPosterItems(Object.keys(selectedPostersMap));

    if (isMobileScreenWidth()) {
      deselectPagesInCurrentMobileView(selectedPostersMap);
    }
  } else {
    if (showPageLimitWarning && shouldDisplayPageLimitWarning()) {
      displayPageLimitWarning();
    } else {
      hidePageLimitWarning();
    }
  }

  Object.entries(selectedPostersMap).forEach(([posterId, data]) => {
    updateStylesForPosterGridItemOnSelection(posterId, data, isMultiselectAllowed, addCheckmarkOnSelection);
  });

  function deselectPagesInCurrentMobileView(selectedPostersMap: Record<string, PageId[]>): void {
    const selectedPages = Object.values(selectedPostersMap).flat();
    const selector = getSelectorForSelectedPagesWithoutIds(selectedPages);
    const selectedMobileViewPages = $(`.js-mobile-pages-view ${selector}`);

    selectedMobileViewPages.removeClass('-selected');
    const mobileCheckmarkIcon = selectedMobileViewPages?.find('.js-page-check-icon');
    mobileCheckmarkIcon?.removeClass('-selected -primary')?.addClass('-secondary');
  }
}

function updateStylesForPosterGridItemOnSelection(designId: string, selectedPages: string[], isMultiselectAllowed = true, addCheckmarkOnSelection = true): void {
  // eslint-disable-next-line no-restricted-globals
  const element = $(`[data-id="${designId}"]`);
  // eslint-disable-next-line no-restricted-globals
  const nameInsideElement = $(`[data-id="${designId}"] .name`).eq(0);

  if (element.hasClass('selected-item')) {
    nameInsideElement.addClass(styles.selectedName);
  } else {
    nameInsideElement.removeClass(styles.selectedName);
  }

  if (addCheckmarkOnSelection) {
    updateCheckmarkTagForPosterGridItem(element, designId);
  }

  if (!isMultiselectAllowed) {
    deselectPreviouslySelectedPages(element, selectedPages);
  }
}

function updateCheckmarkTagForPosterGridItem(element: JQuery, elementDesignId?: string): void {
  const designId = elementDesignId ?? element.attr('data-id');

  if (!designId) {
    console.error('Unable to get design id when updating checkmark for clicked item');
    return;
  }

  if (!element.hasClass('selected-item')) {
    element.find(`i.js-selection-checkmark[data-selected-id="${designId}"]`).remove();
    return;
  }

  if (element.children(`i.js-selection-checkmark[data-selected-id="${designId}"]`).length > 0) {
    return;
  }

  // eslint-disable-next-line no-restricted-globals
  const newDiv = $('<i>').addClass('selection-checkmark js-selection-checkmark icon-check').attr('data-selected-id', designId);
  element.append(newDiv);
}

function deselectPreviouslySelectedPages(element: JQuery, selectedPages: string[]): void {
  const previouslySelectedPagesSelector = getSelectorForSelectedPagesWithoutIds(selectedPages);

  element.find(`.js-pages-popup ${previouslySelectedPagesSelector}`)?.removeClass('-selected');
}

function deSelectPreviouslySelectedPosterItems(selectedPosterIds: string[]): void {
  const previouslySelectedPostersSelector = getSelectorForSelectedPostersWithoutIds(selectedPosterIds);

  // eslint-disable-next-line no-restricted-globals
  const previouslySelectedElements = $(previouslySelectedPostersSelector);

  previouslySelectedElements.each((_index, htmlElement): void => {
    // eslint-disable-next-line no-restricted-globals
    const element = $(htmlElement);
    const elementName = element.find('.name');

    element.children('i.js-selection-checkmark').remove();
    element.removeClass('selected-item');
    elementName.removeClass(styles.selectedName);

    const previouslySelectedPages = element.find('.js-pages-popup .js-page-preview-container.-selected');
    previouslySelectedPages?.removeClass('-selected');

    if (isMobileScreenWidth()) {
      const mobileCheckmarkIcon = previouslySelectedPages?.find('.js-page-check-icon');
      mobileCheckmarkIcon?.removeClass('-selected -primary')?.addClass('-secondary');
    }
  });
}

function getSelectorForSelectedPostersWithoutIds(selectedPosterIds: string[] = []): string {
  let selector = '.js-poster.selected-item';

  selector += selectedPosterIds
    .map((id: string) => {
      return ':not([data-id="' + id + '"])';
    })
    .join();
  return selector;
}

function getSelectorForSelectedPagesWithoutIds(selectedPageIds: string[] = []): string {
  let selector = '.js-page-preview-container.-selected';

  selector += selectedPageIds
    .map((id: string) => {
      return ':not([data-page-id="' + id + '"])';
    })
    .join();
  return selector;
}

function shouldDisplayPageLimitWarning(): boolean {
  const selectedPostersSelector = getSelectorForSelectedPostersWithoutIds(),
    selectedPagesSelector = getSelectorForSelectedPagesWithoutIds(),
    selectedElements = $(selectedPostersSelector);

  let totalSelectedPages = 0;

  selectedElements.each((_, htmlElement): void => {
    // eslint-disable-next-line no-restricted-globals
    const element = $(htmlElement);
    const selectedPagesInElement = element.find(selectedPagesSelector);

    if (selectedPagesInElement?.length) {
      totalSelectedPages += selectedPagesInElement.length;
    } else {
      totalSelectedPages += 1;
    }
  });

  return totalSelectedPages > DESIGN_SELECTOR_PAGES_LIMIT;
}

function displayPageLimitWarning(): void {
  // eslint-disable-next-line no-restricted-globals
  const pageLimitWarning = $('.js-page-limit-message');
  if (pageLimitWarning.length > 0) {
    return;
  }

  const pageLimitText = DESIGN_SELECTOR_PAGES_LIMIT > 1 ? `${DESIGN_SELECTOR_PAGES_LIMIT} pages` : `${DESIGN_SELECTOR_PAGES_LIMIT} page`;
  let messageText = '';
  if (isMobileScreenWidth()) {
    messageText = window.i18next.t('pmwjs_email_editor_page_limit_message_mobile', {pageLimitText});
  } else {
    messageText = window.i18next.t('pmwjs_email_editor_page_limit_message_desktop', {pageLimitText});
  }

  // eslint-disable-next-line no-restricted-globals
  const modalDiv = $('#' + 'select-design-dialog');
  const contentDiv = modalDiv.find('.modal-content > .content');
  // eslint-disable-next-line no-restricted-globals
  const elementToInsert = $(
    `<div class="flex-center content-body-white flex-vertical-center bg-neutral-80 radius-4 spacing-p-l-2 spacing-p-r-2 spacing-p-t-1 spacing-p-b-1 _position-absolute page-limit-message js-page-limit-message fadeInAnimation">
            <i class="icon-info page-limit-message-icon"></i>
            <span class="page-limit-message-text">${messageText}</span>
          </div>`
  );

  contentDiv.prepend(elementToInsert);
  elementToInsert.animate({opacity: 1}, 200);
}

function hidePageLimitWarning(): void {
  // eslint-disable-next-line no-restricted-globals
  const pageLimitWarning = $('.js-page-limit-message');
  if (pageLimitWarning.length > 0) {
    pageLimitWarning.animate({opacity: 0}, 200, function () {
      pageLimitWarning.remove();
    });
  }
}

function isMobileScreenWidth(): boolean {
  return window.PMW.isMobileScreenWidth();
}
